import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useHeightAvailable } from "@repoflow/components/content-resize";
import * as Loading from "@repoflow/components/loading";
import * as Icons from "@repoflow/form-schema/icons";
import { Button, ButtonGroup, Input, Label } from "reactstrap";
import "./style.css";

// The editor half of the preview protocol.
//
// The document being previewed is composed inside an iframe, not here, and the
// two halves talk over postMessage. That is what the archived project did and
// the reason survives the port: a tenant's section carries a `<script>` and a
// `<style>`, and both have to run somewhere that is not this application's
// document. Ported from @repoflow/components/preview + libs/site-mount.
//
// WHAT CROSSES THE BOUNDARY
//
//   parent -> frame   mount     everything needed to compose the document
//                     selected  which element to highlight, and whether to
//                               scroll to it
//                     config    { highlight } — the dimmer on or off
//
//   frame -> parent   preview.mounted   the frame is listening; send `mount`
//                     preview.goto      somebody double-clicked an element
//                     editor.position   the frame scrolled; remember where
//                     editor.lock       an inline edit has focus — do not
//                                       reload the frame out from under it
//                     editor.input      an inline edit changed a value
//
// Every listener checks `e.origin`. The frame is same-origin here, unlike the
// archived project where it was served from the tenant's own domain — but the
// check is what stops any other page that can get a handle on this window from
// driving the editor, so it stays.

// THE TARGET ORIGIN IS NAMED, and since P142 it is not this window's.
//
// The frame is served from the sites hostname — a different origin — so that a
// tenant's `<script>` runs somewhere that is not this application. `"*"` would
// have been the easy way to keep posting across that boundary and it is the
// wrong one: it hands the payload to whatever document is in the frame,
// including one a tenant's script navigated it to. The page being edited is in
// that payload.
export const send = (frame, message, payload, targetOrigin) => {
  if (frame && frame.current && frame.current.contentWindow && targetOrigin) {
    frame.current.contentWindow.postMessage(
      JSON.stringify({ message, payload }),
      targetOrigin
    );
  }
};

// The origin a `src` resolves to. Relative for the same-origin fallback — see
// `previewSrc` in the app's env — absolute once there is a distribution to
// serve the frame from.
export const originOf = (src) => {
  try {
    return new URL(src, window.location.origin).origin;
  } catch (e) {
    return window.location.origin;
  }
};

// Run a compose, and call back once it has settled either way.
//
// `onMounted` is a caller's function and may be synchronous, may return a
// promise, and may throw before it returns one — a `_get` on an answer that
// came back empty is enough. All three have to land on one of the two
// callbacks: before this, a throw propagated out of the effect and a rejection
// went nowhere, and in both cases the spinner that `setLoading(true)` had just
// turned on stayed on with nothing on screen saying why.
const onMountedSettled = (run, onDone, onFailed) => {
  let result;

  try {
    result = run();
  } catch (failure) {
    onFailed(failure);
    return;
  }

  if (!result || typeof result.then !== "function") {
    onDone();
    return;
  }

  result.then(onDone, onFailed);
};

// One route, however the edge decides to spell it. See onFrameLoad.

// How many times a frame that navigates itself away is put back before the pane
// gives up and says so. Three is enough to survive a redirect somebody is in
// the middle of editing out, and few enough that a script redirecting on every
// load stops rather than looping.
const RECOVERY_LIMIT = 3;

const Context = React.createContext(null);

export const usePreview = () => useContext(Context);

// Holds the iframe ref and the reload counter, so a control anywhere on the
// screen can force a reload — the section picker does, because choosing a
// different section changes what renders without changing the page's own
// values, which is what the auto-refresh watches.
export const Provider = ({ children }) => {
  const [key, setKey] = useState(0);
  const frameRef = useRef(null);

  // Set by the Frame from its own `src`. It lives here because `goto` is called
  // from controls that never see the `src` prop — the element list's crosshair
  // is the one in the product — and a send with no target origin posts nothing.
  const originRef = useRef(null);

  const context = useMemo(
    () => ({
      frameRef,
      originRef,
      key,
      refresh: () => setKey((current) => current + 1),
      goto: (selected) =>
        send(frameRef, "selected", { selected, goto: true }, originRef.current),
    }),
    [key]
  );

  return <Context.Provider value={context}>{children}</Context.Provider>;
};

export const Frame = ({
  src,
  // Anything whose change should reload the frame. The page's values, normally.
  watch,
  selected,
  auto,
  highlight,
  onAutoChange,
  onHighlightChange,
  // Called once the frame reports it is listening. Compose the payload here —
  // it is async, because the sections a page references have to be fetched —
  // and hand it to `send`.
  onMounted,
  onGoto,
  onInput,
  hideControls = false,
  // The dimmer picks the SELECTED element out of the others, so a screen that
  // composes ONE element — the section editor — has nothing for it to do. Its
  // toggle is hidden there rather than left on screen changing nothing.
  hideHighlight = false,
}) => {
  const { frameRef, originRef, key: controlKey, refresh: controlRefresh } = usePreview();

  // WHERE THE FRAME IS, which is the whole of P142 in one value. Same-origin
  // when there is no distribution name to build an absolute `src` from — a
  // local checkout, or an environment whose cdn stack has not been deployed —
  // and the cross-origin document everywhere that has one.
  const frameOrigin = useMemo(() => originOf(src), [src]);
  originRef.current = frameOrigin;
  const { refContent, height } = useHeightAvailable();

  const [state, setState] = useState({
    mounted: false,
    key: 0,
    loading: false,
    // Set when `auto` is off and the page has changed since the last reload —
    // it is what turns the reload button into "Refresh preview".
    unsync: false,
    // An inline edit has focus. Reloading would destroy the caret mid-word.
    locked: false,
    // Where the frame was scrolled to, so a reload lands in the same place
    // rather than jumping to the top on every keystroke.
    position: 0,
    // Set when the frame navigated itself away once too often. It is what
    // stops the reload below from composing into the frame it just put back,
    // which would run the redirecting script again and start the loop over.
    // Cleared by a deliberate reload, which is the moment somebody may have
    // just deleted the redirect.
    stopped: false,
    // What went wrong composing the last payload, if anything. `onMounted`
    // fetches — a page's sections and its site — and a fetch fails: the network
    // goes, the token expires, somebody's access to the site is revoked. Before
    // this the rejection went nowhere at all and the only thing on screen was
    // the spinner below, still spinning, on a pane that would never fill.
    error: null,
  });

  const { mounted, key, loading, unsync, locked, position, error } = state;

  // The `watch` value the frame was last GIVEN — set where the mount payload is
  // handed over, below. Anything that differs from it is a change the frame has
  // not seen yet; anything equal to it is already on screen.
  const rendered = useRef(watch);

  // WHICH FRAME A COMPOSE BELONGS TO.
  //
  // `onMounted` is asynchronous — the page editor awaits one query per
  // referenced section plus the site's — and the frame can be replaced while it
  // is in flight: an edit lands, the watcher below fires, the iframe remounts.
  // The compose that was already running does not know that. It finishes, calls
  // `send`, and `send` reads `frameRef.current`, which by then is the NEW
  // frame — so a document composed from the value BEFORE the edit is posted
  // into the frame that was reloaded to show the value after it.
  //
  // Measured, because the ordering is the whole of it: with two composes in
  // flight the payloads arrived [new, old], so the pane ended up showing the
  // stale document — and `rendered.current` had already been advanced to the
  // new value, so the watcher saw nothing to reload for and the pane stayed
  // stale until the next keystroke. The editor believed it had rendered
  // something it had not.
  //
  // Every reload mints a generation. A compose whose generation is no longer
  // the one on screen delivers nothing and changes no state; the frame that
  // replaced it announces itself and composes its own. That is the retry —
  // there is no queue to drain and no payload to hold, because the only thing
  // worth sending is the one composed from what is in the form now.
  const generation = useRef(0);

  // How many times the frame has been put back after navigating itself away
  // since the last DELIBERATE reload. See onFrameLoad below.
  const recoveries = useRef(0);

  const reload = useCallback(() => {
    // Here rather than in an effect on `key`: a recovery bumps `key` too, so an
    // effect would reset the budget on every recovery and the cap would never
    // be reached. What resets it is somebody asking for a reload — an edit, or
    // the button — which is the moment the script that was redirecting may have
    // been changed.
    recoveries.current = 0;

    setState((current) => ({
      ...current,
      mounted: false,
      stopped: false,
      key: current.key + 1,
      unsync: false,
      error: null,
    }));
  }, []);

  // Debounced, and memoised on `reload` which is stable: a burst of keystrokes
  // in the markup field is one reload, not one per character.
  const debouncedReload = useMemo(() => {
    let timer = null;

    const fn = () => {
      clearTimeout(timer);
      timer = setTimeout(reload, 500);
    };

    fn.cancel = () => clearTimeout(timer);
    return fn;
  }, [reload]);

  useEffect(() => () => debouncedReload.cancel(), [debouncedReload]);

  const messageHandler = useCallback(
    (event) => {
      // THE FRAME'S ORIGIN, not this one. Same value until P142 gave the frame
      // a hostname of its own; the check is what stops any other document that
      // can get a handle on this window from driving the editor, and it is
      // narrower than it was rather than wider — `editor.input` writes into the
      // page's value object.
      if (event.origin !== frameOrigin) {
        return;
      }

      let msg;
      try {
        msg = JSON.parse(event.data);
      } catch (e) {
        // Not ours. Other things post to window — dev-server overlays do.
        return;
      }

      if (!msg || typeof msg.message !== "string") {
        return;
      }

      if (msg.message === "preview.mounted") {
        setState((current) =>
          current.stopped
            ? current
            : { ...current, mounted: true, unsync: false }
        );
      }

      if (msg.message === "preview.goto") {
        onGoto && onGoto(parseInt(msg.payload.index, 10));
      }

      if (msg.message === "editor.position") {
        setState((current) => ({ ...current, position: msg.payload.position }));
      }

      if (msg.message === "editor.lock") {
        setState((current) => ({ ...current, locked: msg.payload.locked }));
      }

      if (msg.message === "editor.input") {
        onInput && onInput(msg.payload);
      }
    },
    [onGoto, onInput, frameOrigin]
  );

  useEffect(() => {
    window.addEventListener("message", messageHandler, false);
    return () => window.removeEventListener("message", messageHandler, false);
  }, [messageHandler]);

  // The frame said it is listening. Compose and send.
  useEffect(() => {
    if (!mounted) {
      return;
    }

    const mine = generation.current + 1;
    generation.current = mine;

    // Is the frame this compose was started for still the frame on screen? See
    // the note on `generation`. Every effect of a compose goes through it —
    // the delivery, the spinner and the error alike — because a stale compose
    // that cannot post a document can still turn the spinner off under the one
    // that replaced it.
    const current = () => generation.current === mine;

    // BEFORE the awaits inside `onMounted`, not after, and this is load-bearing
    // in the other direction from what it looks like. It does not claim the
    // document has been rendered; it records which value the frame on screen
    // has been ASKED to render, and the watcher below runs in this same commit.
    //
    // Moving it to the delivery — which is what "the payload can be dropped and
    // the editor believes it rendered it" reads like it wants — puts the pane
    // into the 500ms flicker loop reload.test.js exists for. Measured: an
    // `onMounted` that has not settled within the debounce window, which is any
    // page of several sections on a slow link, went from 2 composes to 13 and
    // climbing. The watcher fires on `mounted` going false -> true, sees a
    // value the in-flight compose has not recorded yet, and schedules the
    // reload that starts the next one.
    //
    // What was actually wrong is below: nothing dropped the compose belonging
    // to a frame that had been replaced.
    rendered.current = watch;

    const setLoading = (value) =>
      setState((state) => (current() ? { ...state, loading: value } : state));

    onMountedSettled(
      () =>
        onMounted &&
        onMounted({
          selected,
          position,
          setLoading,
          // Everything a compose delivers goes through here, so a compose
          // whose frame is gone posts nothing. `send` reads
          // `frameRef.current`, which by then is the frame that REPLACED it —
          // the stale document would be written over the fresh one, and the
          // watcher above, holding the fresh value, would find nothing to
          // reload for.
          send: (message, payload) => {
            if (!current()) {
              return false;
            }

            send(frameRef, message, payload, frameOrigin);
            return true;
          },
        }),
      () => {
        if (!current()) {
          return;
        }

        setState((state) => ({ ...state, loading: false, error: null }));
      },
      (failure) => {
        if (!current()) {
          return;
        }

        setState((state) => ({
          ...state,
          loading: false,
          error: (failure && failure.message) || String(failure),
        }));
      }
    );
    // `selected` and `position` are read at mount time only: re-running this on
    // either would re-send the whole document every time somebody clicks an
    // element. The two effects below are what keep them current.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mounted, key]);

  useEffect(() => {
    if (mounted) {
      send(frameRef, "selected", { selected, goto: true }, frameOrigin);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, mounted, loading]);

  useEffect(() => {
    if (mounted) {
      send(frameRef, "config", { highlight }, frameOrigin);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlight, mounted]);

  // The page changed. Reload, unless somebody is typing INTO the preview, or
  // has turned auto-refresh off.
  useEffect(() => {
    if (!mounted) {
      return;
    }

    // Nothing has changed since the frame was last composed, so there is
    // nothing to reload for. Without this the effect fired on its OWN result:
    // `mounted` is in the dependency array and goes false -> true on every
    // reload, when the fresh frame announces itself. That scheduled the next
    // reload, which scheduled the next — the preview blanked and re-rendered
    // on a 500ms loop forever, whether or not anything had been edited, which
    // is the flicker. (Gen-2 avoided it by watching `[site, page]` only; the
    // port added `mounted` to keep the reference honest and closed the loop.)
    //
    // Guarding on the VALUE rather than dropping `mounted` from the array also
    // keeps the two toggles honest: turning auto-refresh off with nothing
    // pending no longer lights up "Refresh preview", and turning it back on
    // with an edit pending syncs immediately.
    if (watch === rendered.current) {
      return;
    }

    if (!auto) {
      setState((current) => ({ ...current, unsync: true }));
      return;
    }

    if (!locked) {
      debouncedReload();
    }
    // `locked` is deliberately not a dependency: it changes on focus and blur
    // of an inline edit, and reloading on blur would undo the edit's own
    // debounce. It is read, not watched.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch, auto, mounted]);

  // Somebody called refresh() on the context — a section picker, normally, or
  // the button in the toolbar.
  //
  // `mounted` is what keeps this from firing on the first render, when
  // `controlKey` arrives at 0 and the frame is loading anyway. `error` is the
  // other state a reload is meaningful from and it is NOT mounted: a frame that
  // navigated away one time too many, or a compose that failed, is exactly the
  // pane somebody presses this button on. Without it the button was dead in the
  // one place it was needed.
  useEffect(() => {
    if (mounted || error) {
      reload();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlKey]);

  // THE FRAME NAVIGATED AWAY. Put it back.
  //
  // A tenant's script runs inside the preview and `location.href = "/somewhere"`
  // is one line of it — deliberate on a served page, where it is a redirect the
  // author wrote, and inside the editor it is the whole right-hand pane going
  // blank with no way back short of leaving the screen.
  //
  // The composer used to rewrite `document.location =` out of the markup on its
  // way into the frame. That is a denylist: `window.location`, `location.href`,
  // `location.assign()` and `top.location` all walk past it, and so does the
  // same string written in a `code` element's Script field, which the rewrite
  // never ran over. Nothing inside a same-origin frame can stop this — the
  // frame is allowed to navigate itself, and no sandbox flag exists that
  // forbids it without also taking away the same-origin access the editor needs.
  //
  // So it is caught after the fact instead, which is the one place it can be.
  // The frame is same-origin, so `load` fires here for every document it loads
  // and its location is readable; anything that is not the preview route is a
  // navigation nobody asked for, and remounting the iframe puts the editor's
  // own document back. A cross-origin destination makes the read THROW, which
  // is the same answer and the case the rewrite could never have covered.
  //
  // Bounded, because a script that navigates on load would otherwise be
  // restored into running again forever. After RECOVERY_LIMIT the pane says so
  // and stops; the reload button is still there for somebody who has since
  // changed the script.
  const onFrameLoad = useCallback(() => {
    const node = frameRef.current;

    if (!node) {
      return;
    }

    // NOTHING IS RECOVERED BEFORE THE FRAME HAS BEEN GIVEN A DOCUMENT, and this
    // is still the guard that matters.
    //
    // A tenant's script cannot have navigated a frame it has not been mounted
    // into — the preview route renders nothing at all until `mount` arrives. So
    // a load while `mounted` is false is the frame arriving, not leaving.
    //
    // It is also what makes the test below the whole test: `mounted` is false
    // for every load the editor asked for, because `reload` clears it in the
    // same commit that bumps `key`, and true only once the frame has announced
    // itself. A SECOND load after that announcement is therefore a document
    // nobody asked for.
    if (!mounted) {
      return;
    }

    // HOW THIS USED TO WORK, AND WHY IT CANNOT ANY MORE.
    //
    // It read `contentWindow.location` and compared the path with `src`. That
    // needs the frame to be same-origin, which since P142 it is not: the read
    // throws for every document the frame legitimately holds, so every load
    // would look like a navigation, every load would be "recovered", and three
    // of those would blank the pane on every screen that has one. The record
    // has that failure already — a wrong comparison here cost eight e2e specs —
    // and cross-origin turns it from a wrong comparison into an impossible one.
    //
    // So the signal is the handshake instead of the URL, and it says the same
    // thing without reading anything: the frame announces itself with
    // `preview.mounted` once per document, and `mounted` is cleared by every
    // reload the editor performs. A load arriving while `mounted` is still true
    // is a second document in a frame that had already announced — which is a
    // navigation, whoever caused it.
    //
    // Strictly better than the old check in one way worth stating: it catches a
    // same-origin navigation to another route of this app, which the path
    // comparison also caught, AND a cross-origin one, which the old code could
    // only see as a thrown read. It is blind to a frame that navigates and
    // still announces itself as the preview route — which is a document serving
    // this app's own preview bundle, i.e. not a tenant's page.
    recoveries.current += 1;

    if (recoveries.current > RECOVERY_LIMIT) {
      // Still remounted, so the pane is the editor's own empty route rather
      // than whatever the script navigated to — a third party's page rendered
      // where the preview was is worse than a blank one. `stopped` is what
      // keeps it empty: the frame will announce itself and be ignored.
      setState((state) => ({
        ...state,
        mounted: false,
        stopped: true,
        loading: false,
        key: state.key + 1,
        error:
          "The preview navigated away from the editor and was brought back " +
          `${RECOVERY_LIMIT} times. Something on this page is redirecting; ` +
          "the preview is stopped until you reload it.",
      }));
      return;
    }

    setState((state) => ({
      ...state,
      mounted: false,
      key: state.key + 1,
      error: null,
    }));
  }, [mounted]);

  return (
    <>
      {!hideControls && (
        <div className="preview-controls">
          <div className="form-check form-check-inline">
            <Label check>
              <Input
                type="checkbox"
                checked={auto}
                onChange={() => onAutoChange && onAutoChange(!auto)}
              />{" "}
              Auto refresh
            </Label>
          </div>

          <ButtonGroup>
            <Button color="link" onClick={() => controlRefresh()}>
              {loading ? (
                <Loading.Icon />
              ) : (
                <span>
                  <Icons.Reload /> {unsync ? " Refresh preview" : ""}
                </span>
              )}
            </Button>
          </ButtonGroup>

          {!hideHighlight && (
            <div className="form-check form-check-inline">
              <Label check>
                <Input
                  type="checkbox"
                  checked={highlight}
                  onChange={() => onHighlightChange && onHighlightChange(!highlight)}
                />{" "}
                Highlight
              </Label>
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="preview-error" role="alert">
          The preview could not be composed: {error}
        </div>
      )}

      <div ref={refContent}></div>
      <iframe
        key={key}
        title="Preview"
        className="preview-canvas"
        ref={frameRef}
        src={src}
        onLoad={onFrameLoad}
        style={{ width: "100%", height: `${height}px` }}
      />
    </>
  );
};
